import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useBoundedPoll } from '../../../../../../common/hooks/useBoundedPoll';
import { PAN_PROFILE_COPY, PAN_PROFILE_ENRICHED_AT_FIELD } from '../../../../../../common/ManagementProfile/managementProfileFields';
import { fetchManagementProfilesV2 } from '../../../../../../store/dashboard/overview/overview_apiHelper';
import { isIndividualPan } from '../../../../../../utils/validatePAN';

interface EnrichmentHold {
    managementProfileId: string;
    pollKey: string;
}

/*
   Adding a director with an individual PAN starts a server-side enrichment that names the row a
   few seconds AFTER the create response returns, so a refetch issued on save is ordered before
   the name can exist and can never observe it.

   The bureau list this tab renders is the wrong thing to watch for it, twice over. It exposes
   neither `managementProfileId` nor the `panProfileEnrichedAt` stamp — polus binds it into a
   typed DTO declaring neither, so Gson drops both — leaving only an inferential name-diff that
   a duplicate name or a concurrent enrichment would both get wrong. And refetching it flips
   `loadingDirectorsOverviewList`, which trips this screen's `isLoading` shimmer and UNMOUNTS the
   very modal the hold is keeping open, wiping a reopened form's typed input with it.

   So the hold watches the management profile directly, on the same stamp and the same terms as
   the Management Profile tab, and the bureau list is refetched exactly once — on settle, when it
   finally has something new to show.
*/
export function useAddDirectorEnrichmentHold(orgId: string | undefined, onSettled: (enrichedName?: string) => void) {
    const [hold, setHold] = useState<EnrichmentHold | null>(null);
    const holdRef = useRef<EnrichmentHold | null>(null);

    useEffect(() => {
        holdRef.current = hold;
    }, [hold]);

    // The enriched name is passed on because the caller has no other handle back to the new row:
    // the bureau list carries neither a managementProfileId nor, for a hand-added director, a
    // directorId. Absent whenever the enrichment was never confirmed.
    const settle = useCallback(
        (enrichedName?: string) => {
            setHold(null);
            onSettled(enrichedName);
        },
        [onSettled],
    );

    const giveUp = useCallback(() => {
        settle();
        toast.info(PAN_PROFILE_COPY.UNCONFIRMED);
    }, [settle]);

    /*
       Armed only for a create that carried an id, so the profile is brand new and cannot already
       carry a stamp — which is why any stamp at all settles this, with no baseline to compare.
    */
    const checkEnrichment = useCallback(() => {
        const armed = holdRef.current;
        if (!orgId || !armed) return;
        fetchManagementProfilesV2({ orgId }).then((res: any) => {
            if (holdRef.current?.pollKey !== armed.pollKey) return;
            const profile = res?.managementProfile?.profiles?.find((row: any) => row?.managementProfileId === armed.managementProfileId);
            if (profile?.customFields?.[PAN_PROFILE_ENRICHED_AT_FIELD]) settle(profile.name);
        });
    }, [orgId, settle]);

    useBoundedPoll(checkEnrichment, !!hold, giveUp, { stickyAttemptsKey: hold?.pollKey });

    /*
       Armed only for a PAN the backend actually enriches, and only for a create that carried an
       id — a DUPLICATE outcome carries none. Returns whether it armed: a caller holding UI open
       must key off this and never re-derive it, or a create without an id leaves that UI waiting
       for good. Each arm gets a fresh pollKey so a second add buys a full attempt budget.
    */
    const armEnrichmentHold = useCallback((managementProfileId?: string, pan?: string | null): boolean => {
        if (!managementProfileId || !isIndividualPan(pan)) return false;
        setHold({ managementProfileId, pollKey: `${managementProfileId}:${Date.now()}` });
        return true;
    }, []);

    return { awaitingEnrichment: !!hold, armEnrichmentHold };
}
